import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Copy, Sparkles, Loader2, RefreshCw } from "lucide-react";

const SCORE = {
  chaud: "border-red-400/40 bg-red-400/10 text-red-300",
  tiède: "border-amber-400/40 bg-amber-400/10 text-amber-300",
  froid: "border-sky-400/40 bg-sky-400/10 text-sky-300",
};

export const scoreCls = (s) => SCORE[s] || "border-white/20 text-white/60";

const Block = ({ label, text, testid }) => (
  <div className="rounded-xl border border-white/10 bg-white/5 p-4">
    <div className="flex items-center justify-between">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40">{label}</p>
      <button
        onClick={() => navigator.clipboard?.writeText(text).then(() => toast.success("Copié"))}
        className="flex items-center gap-1 text-[11px] text-white/50 hover:text-white"
      >
        <Copy className="h-3 w-3" /> Copier
      </button>
    </div>
    <p data-testid={testid} className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-white/85">{text}</p>
  </div>
);

export default function LeadAiDrawer({ lead, busy, onClose, onRefresh }) {
  const ai = lead?.ai;
  return (
    <AnimatePresence>
      {lead && (
        <motion.aside
          data-testid="admin-ai-drawer"
          initial={{ x: 40, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 40, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-y-0 right-0 z-[70] flex w-full max-w-md flex-col border-l border-white/10 bg-forest-dark text-white shadow-2xl"
        >
          <div className="flex items-start justify-between border-b border-white/10 px-6 py-5">
            <div>
              <p className="flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-violet-300">
                <Sparkles className="h-3.5 w-3.5" /> Analyse IA Claude
              </p>
              <h3 className="mt-1 font-display text-xl font-bold">{lead.contact_name}</h3>
              <p className="text-xs text-white/50">{lead.phone} · {lead.postal_code}</p>
            </div>
            <button data-testid="admin-ai-close" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full border border-white/10 text-white/60 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            {busy && !ai && (
              <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-5 text-sm text-white/70">
                <Loader2 className="h-4 w-4 animate-spin text-violet-300" /> Claude analyse le prospect…
              </div>
            )}
            {ai && (
              <>
                <div className="flex flex-wrap items-center gap-3">
                  <span data-testid="admin-ai-score" className={`rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wider ${scoreCls(ai.score)}`}>
                    Prospect {ai.score}
                  </span>
                  <span className="text-[11px] text-white/40">
                    {ai.analyzed_at ? new Date(ai.analyzed_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : ""}
                  </span>
                  <button data-testid="admin-ai-refresh" onClick={onRefresh} disabled={busy} className="ml-auto flex items-center gap-1.5 text-[11px] text-white/50 hover:text-white disabled:opacity-50">
                    <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} /> Relancer
                  </button>
                </div>
                <div className="rounded-xl border border-violet-400/20 bg-violet-400/5 p-4">
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-violet-300">Résumé</p>
                  <p data-testid="admin-ai-summary" className="mt-2 text-sm leading-relaxed text-white/90">{ai.summary}</p>
                  {Array.isArray(ai.reasons) && ai.reasons.length > 0 && (
                    <ul className="mt-3 space-y-1 text-xs text-white/60">
                      {ai.reasons.map((r, i) => (<li key={i}>• {r}</li>))}
                    </ul>
                  )}
                </div>
                <Block label="Brouillon SMS" text={ai.draft_sms} testid="admin-ai-draft-sms" />
                <Block label="Brouillon WhatsApp" text={ai.draft_whatsapp} testid="admin-ai-draft-whatsapp" />
              </>
            )}
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
